import { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControlLabel,
  Switch,
  Grid,
  Stack,
} from "@mui/material";
import { CropPortrait, CloudUpload } from "@mui/icons-material";
import { toastAlert } from "../../../../utils/AlertContext";
import { useFooterStyles } from "../../../../hooks/panelEditor/useFooterStyles";
import FontControls from "../globalStylesSection/components/FontControls";
import PositionControls from "../globalStylesSection/components/PositionControls";
import SizeControl from "../globalStylesSection/components/SizeControl";
import {
  AVAILABLE_FONTS,
  POSITIONS
} from "../globalStylesSection/constants/styleDefaults";

const Footer = ({ token, idDocument, openColorModal }) => {
  const {
    footerStyles: serverFooterStyles,
    updateFooterStyles,
    loading: loadingFooter,
    saving: savingFooter,
    refresh: refreshFooter,
  } = useFooterStyles(token, idDocument);

  const [footerStyles, setFooterStyles] = useState({
    active: false,
    position: "center",
    font: "Open Sans",
    color: "#000000",
    fontSize: 12,
    fontWeight: "normal",
    pleca: "#1976d2",
    image: "",
    imageFile: null,
    width: "",
    height: "",
  });

  useEffect(() => {
    if (serverFooterStyles) {
      setFooterStyles((prev) => ({
        ...prev,
        active: serverFooterStyles.active ?? false,
        position: serverFooterStyles.position || "center",
        font: serverFooterStyles.font || "Open Sans",
        color: serverFooterStyles.color || "#000000",
        fontSize:
          serverFooterStyles.fontSize || serverFooterStyles.font_size || 12,
        fontWeight:
          serverFooterStyles.fontWeight ||
          serverFooterStyles.font_weight ||
          "normal",
        pleca: serverFooterStyles.pleca || "#1976d2",
        image: serverFooterStyles.image || "",
        imageFile: null,
        width: serverFooterStyles.width ?? "",
        height: serverFooterStyles.height ?? "",
      }));
    }
  }, [serverFooterStyles]);

  const handleFooterChange = (updates) => {
    setFooterStyles((prev) => ({ ...prev, ...updates }));
  };

  const handleImageUpload = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (ev) =>
          handleFooterChange({
            image: ev.target.result,
            imageFile: file,
          });
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  const handleSaveFooter = async () => {
    try {
      const sanitizedWidth =
        footerStyles.width !== "" &&
        footerStyles.width !== null &&
        !isNaN(Number(footerStyles.width))
          ? Number(footerStyles.width)
          : undefined;

      const sanitizedHeight =
        footerStyles.height !== "" &&
        footerStyles.height !== null &&
        !isNaN(Number(footerStyles.height))
          ? Number(footerStyles.height)
          : undefined;

      const result = await updateFooterStyles({
        active: footerStyles.active,
        position: footerStyles.position,
        font: footerStyles.font,
        color: footerStyles.color,
        fontSize: footerStyles.fontSize,
        fontWeight: footerStyles.fontWeight,
        pleca: footerStyles.pleca,
        imageFile: footerStyles.imageFile,
        image: footerStyles.image,
        width: sanitizedWidth,
        height: sanitizedHeight,
      });
      if (result.success) {
        refreshFooter();
        toastAlert("Footer guardado exitosamente", "success");
      } else {
        toastAlert(result.error || "Error al guardar el footer", "error");
      }
    } catch (error) {
      toastAlert("Error al guardar el footer", "error");
    }
  };

  return (
    <Card sx={{ borderRadius: "16px", bgcolor: "#ffffff" }}>
      <CardContent sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <CropPortrait sx={{ fontSize: 20, color: "#1976d2" }} />
          <Typography variant="h6" fontWeight="bold">
            Footer
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Configura el footer del documento: activación, imagen, fuente y
          posición.
        </Typography>
        <Stack spacing={3}>
          <FormControlLabel
            control={
              <Switch
                checked={footerStyles?.active || false}
                onChange={(e) =>
                  handleFooterChange({ active: e.target.checked })
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#1976d2",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    bgcolor: "#1976d2",
                  },
                }}
              />
            }
            label={footerStyles?.active ? "Desactivar footer" : "Activar footer"}
          />
          {footerStyles?.active && (
            <>
            <Grid container alignItems="center" justifyContent="center">
                {footerStyles?.image && (
                  <Box mt={2}>
                    <img
                      src={footerStyles.image}
                      alt="Footer preview"
                      style={{ maxHeight: 60 }}
                    />
                  </Box>
                )}
              </Grid>
              <Button
                variant="contained"
                startIcon={<CloudUpload sx={{ fontSize: 20 }} />}
                onClick={handleImageUpload}
                sx={{
                  bgcolor: "#1976d2",
                  color: "white",
                  borderRadius: "50px",
                  px: 4,
                  py: 1.5,
                  textTransform: "capitalize",
                  "&:hover": { bgcolor: "#1565c0" },
                }}
              >
                {footerStyles?.image ? "Cambiar imagen" : "Añadir imagen"}
              </Button>
              
              <FontControls
                font={footerStyles.font}
                fontSize={footerStyles.fontSize}
                fontWeight={footerStyles.fontWeight}
                color={footerStyles.color}
                fonts={AVAILABLE_FONTS}
                onFontChange={(value) => handleFooterChange({ font: value })}
                onFontSizeChange={(value) =>
                  handleFooterChange({ fontSize: value })
                }
                onFontWeightChange={(value) =>
                  handleFooterChange({ fontWeight: value })
                }
                onOpenColorPicker={() =>
                  openColorModal(
                    "footer",
                    footerStyles.color || "#000000",
                    (newColor) => handleFooterChange({ color: newColor }),
                    "Seleccionar color de texto"
                  )
                }
              />

              <PositionControls
                value={footerStyles?.position}
                onChange={(value) => handleFooterChange({ position: value })}
                label="Posición del footer"
                positions={POSITIONS}
              />
              <SizeControl
                width={footerStyles?.width}
                height={footerStyles?.height}
                onChangeWidth={(w) =>
                  handleFooterChange({ width: w, active: true })
                }
                onChangeHeight={(h) =>
                  handleFooterChange({ height: h, active: true })
                }
                label="Dimensiones del footer (cm)"
              />
            </>
          )}
          <Grid container justifyContent="flex-end">
                <Button
                  variant="contained"
                  onClick={handleSaveFooter}
                  disabled={savingFooter || loadingFooter}
                  sx={{
                    mt: 2,
                    bgcolor: "#1976d2",
                    color: "white",
                    borderRadius: "50px",
                    px: 4,
                    py: 1.5,
                    textTransform: "capitalize",
                    "&:hover": { bgcolor: "#1565c0" },
                    "&:disabled": {
                      bgcolor: "#e0e0e0",
                      color: "#9e9e9e",
                    },
                  }}
                >
                  {savingFooter ? "Guardando..." : "Guardar Cambios"}
                </Button>
              </Grid>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default Footer;